import { FolderOpenOutlined } from '@ant-design/icons';
import { Tooltip, Typography } from 'antd';
import React from 'react';
import styled from 'styled-components';
import { Entity } from '../../../types.generated';
import { ANTD_GRAY } from '../../entity/shared/constants';
import { useEntityRegistry } from '../../useEntityRegistry';

const ParentNodesWrapper = styled.div`
    font-size: 14px;
    color: ${ANTD_GRAY[7]};
    display: flex;
    align-items: center;
    overflow: hidden;
`;

const ParentNode = styled(Typography.Text)<{ color?: string }>`
    margin-left: 4px;
    color: ${(props) => (props.color ? props.color : ANTD_GRAY[7])};
`;

export const ArrowWrapper = styled.span`
    margin: 0 3px;
`;

const StyledTooltip = styled(Tooltip)`
    display: flex;
    white-space: nowrap;
    overflow: hidden;
`;

const DEFAULT_NUM_VISIBLE = 2;

interface Props {
    parentEntities: Entity[];
    numVisible?: number;
    showEndingSeparator?: boolean;
}

export default function ParentEntities({
    parentEntities,
    numVisible = DEFAULT_NUM_VISIBLE,
    showEndingSeparator,
}: Props) {
    const entityRegistry = useEntityRegistry();

    // parent nodes/domains are returned with direct parent first
    const orderedParentEntities = [...parentEntities].reverse();
    const numHiddenEntities = orderedParentEntities.length - numVisible;
    const hasHiddenEntities = numHiddenEntities > 0;
    const visibleNodes = hasHiddenEntities ? orderedParentEntities.slice(numHiddenEntities) : orderedParentEntities;

    if (!parentEntities.length) return null;

    return (
        <StyledTooltip
            placement="top"
            overlayStyle={hasHiddenEntities ? { maxWidth: 450 } : { display: 'none' }}
            title={
                <>
                    {orderedParentEntities.map((parentEntity, index) => (
                        <>
                            <FolderOpenOutlined />
                            <ParentNode className="f-color-white">
                                {entityRegistry.getDisplayName(parentEntity.type, parentEntity)}
                            </ParentNode>
                            {index !== orderedParentEntities.length - 1 && <ArrowWrapper>{'>'}</ArrowWrapper>}
                        </>
                    ))}
                </>
            }
        >
            <ParentNodesWrapper>
                {hasHiddenEntities &&
                    [...Array(numHiddenEntities)].map(() => (
                        <>
                            <FolderOpenOutlined />
                            <ArrowWrapper>{'>'}</ArrowWrapper>
                        </>
                    ))}
                {visibleNodes.map((parentEntity, index) => {
                    const displayName = entityRegistry.getDisplayName(parentEntity.type, parentEntity);
                    return (
                        <>
                            <FolderOpenOutlined />
                            <ParentNode ellipsis={!hasHiddenEntities ? { tooltip: displayName } : true}>
                                {displayName}
                            </ParentNode>
                            {(showEndingSeparator || index !== visibleNodes.length - 1) && (
                                <ArrowWrapper className="f-color-high-contrast">{'>'}</ArrowWrapper>
                            )}
                        </>
                    );
                })}
            </ParentNodesWrapper>
        </StyledTooltip>
    );
}
